import React, { useEffect, useState } from 'react';
import { api, type StripeConnectStatus } from '../api';
import { getTheme, useThemeMode } from '../theme';

/**
 * Ekonomi-fliken: markägaren kopplar ett eget Stripe-konto (Express). Köp av markens annonser
 * går sedan direkt till kontot minus plattformsavgiften. Utan kopplat konto kan annonserna inte köpas.
 */
export function StripeConnectPanel({ markId, canConnect }: { markId: string; canConnect: boolean }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [st, setSt] = useState<StripeConnectStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { api.markStripeConnect(markId).then(setSt).catch(() => {}); }, [markId]);

  const go = async (fn: () => Promise<string>) => {
    setBusy(true); setErr('');
    try { window.location.href = await fn(); }
    catch (e: any) { setErr(e.message || 'Något gick fel'); setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true); setErr('');
    try { setSt(await api.refreshStripeAccount(markId)); } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };

  if (!st) return null;
  const ready = st.connected && st.chargesEnabled;
  const btn: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 };

  return (
    <div data-testid="stripe-connect" style={{ marginBottom: 24, padding: 16, border: `1px solid ${ready ? T.success : T.amber}`, borderRadius: 10, background: T.surface, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 16, fontWeight: 800 }}>Kortbetalning via Stripe</div>
      {st.needsReconnect && <div data-testid="stripe-reconnect" style={{ fontSize: 14, fontWeight: 700, color: T.amber }}>Stripe har gått över till skarpt läge – koppla ditt konto igen</div>}
      <div style={{ fontSize: 14, color: T.textSub }}>
        {ready
          ? `Kontot är kopplat. Köp av markens annonser betalas ut till kontot minus Qvenaris avgift.${st.payoutsEnabled ? '' : ' Utbetalningar är inte aktiverade ännu – komplettera uppgifterna hos Stripe.'}`
          : st.connected
            ? 'Kontot är påbörjat men inte klart. Slutför uppgifterna hos Stripe för att kunna ta emot betalningar.'
            : 'Koppla ett Stripe-konto för att jägare ska kunna köpa markens annonser. Pengarna går direkt till kontot, minus Qvenaris avgift.'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {canConnect && !ready && <button disabled={busy} onClick={() => go(() => api.startStripeOnboarding(markId))} style={{ ...btn, border: 'none', background: T.amber, color: '#fff' }}>{st.connected ? 'Slutför hos Stripe' : 'Koppla Stripe-konto'}</button>}
        {canConnect && st.connected && <button disabled={busy} onClick={() => go(() => api.stripeDashboardLink(markId))} style={{ ...btn, border: `1px solid ${T.border}`, background: 'transparent', color: T.text }}>Öppna Stripe</button>}
        {st.connected && <button disabled={busy} onClick={refresh} style={{ ...btn, border: `1px solid ${T.border}`, background: 'transparent', color: T.text }}>Uppdatera status</button>}
      </div>
      {!canConnect && !ready && <div style={{ fontSize: 13, color: T.textSub }}>Bara markägaren kan koppla kontot.</div>}
      {err && <div style={{ fontSize: 13, color: T.danger }}>{err}</div>}
    </div>
  );
}
